import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { affiliateApi } from 'services/api';
import { useAuth } from 'context/AuthContext';
import Logo from 'components/Logo';
import AppHeader from 'components/ui/Header';
import useSEO from 'hooks/useSEO';

const PROMOTION_OPTIONS = [
  'Finance blog / website',
  'YouTube channel',
  'Email newsletter',
  'Twitter / X',
  'Instagram',
  'TikTok',
  'Podcast',
  'Reddit community',
  'Facebook group',
  'Other',
];

export default function AffiliateApplyPage() {
  useSEO({ title: 'Apply — Affiliate Program', noindex: true });

  const { isAuthenticated, isLoading, user } = useAuth();

  const [existing, setExisting]     = useState(null);
  const [checking, setChecking]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(null);
  const [error, setError]           = useState('');

  const [form, setForm] = useState({
    website_url:      '',
    promotion_method: '',
    promotion_type:   '',
    agreed:           false,
  });

  // Fetch affiliate status only when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setChecking(false);
      return;
    }
    affiliateApi.getStatus()
      .then(r => setExisting(r.data?.data?.affiliate))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [isAuthenticated]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreed) { setError('Please agree to the affiliate terms to continue.'); return; }
    if (!form.promotion_method.trim()) { setError('Please describe how you plan to promote us.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await affiliateApi.apply({
        website_url:      form.website_url,
        promotion_method: `[${form.promotion_type}] ${form.promotion_method}`,
      });
      setDone(res.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading spinner while auth initialises or status is being checked
  if (isLoading || checking) {
    return <Shell><div style={{ textAlign: 'center', padding: '80px 24px', color: '#94A3B8' }}>Loading…</div></Shell>;
  }

  // ── Not logged in: inline sign-in prompt — NO redirect ────────────────────
  if (!isAuthenticated) {
    return (
      <Shell>
        <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🤝</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0F172A', marginBottom: 12, letterSpacing: '-0.02em' }}>
            Join Our Affiliate Program
          </h1>
          <p style={{ color: '#64748B', fontSize: 15, lineHeight: 1.7, marginBottom: 8 }}>
            Earn <strong style={{ color: '#0F172A' }}>25% recurring commission</strong> for every subscriber you refer.
          </p>
          <p style={{ color: '#64748B', fontSize: 14, lineHeight: 1.7, marginBottom: 36 }}>
            Sign in or create a free account to complete your application — it only takes a minute.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320, margin: '0 auto' }}>
            <Link
              to="/register"
              state={{ from: '/affiliates/apply' }}
              style={{
                display: 'block', padding: '13px 0',
                background: '#1a7f4b', color: '#fff',
                borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: 'none', textAlign: 'center',
              }}
            >
              Create a Free Account →
            </Link>
            <Link
              to="/login"
              state={{ from: '/affiliates/apply' }}
              style={{
                display: 'block', padding: '13px 0',
                background: '#fff', color: '#0F172A',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontWeight: 600, fontSize: 15,
                textDecoration: 'none', textAlign: 'center',
              }}
            >
              Sign In to Existing Account
            </Link>
          </div>

          <p style={{ marginTop: 28, fontSize: 13, color: '#94A3B8' }}>
            Already an affiliate?{' '}
            <Link to="/login" state={{ from: '/affiliates/dashboard' }} style={{ color: '#1a7f4b', fontWeight: 600, textDecoration: 'none' }}>
              Sign in to your dashboard
            </Link>
          </p>
        </div>
      </Shell>
    );
  }

  // ── Already has an application ────────────────────────────────────────────
  if (existing) {
    const statusColor = { approved: '#1a7f4b', pending: '#D97706', rejected: '#DC2626', suspended: '#DC2626' };
    return (
      <Shell>
        <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>
            {existing.status === 'approved' ? '🎉' : existing.status === 'pending' ? '⏳' : '❌'}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>
            {existing.status === 'approved'
              ? "You're an Approved Affiliate!"
              : existing.status === 'pending'
              ? 'Application Under Review'
              : 'Application ' + existing.status.charAt(0).toUpperCase() + existing.status.slice(1)}
          </h1>
          <div style={{
            display: 'inline-block',
            background: statusColor[existing.status] + '1a',
            color: statusColor[existing.status],
            fontSize: 12, fontWeight: 700, padding: '4px 14px',
            borderRadius: 20, marginBottom: 20,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            {existing.status}
          </div>
          <p style={{ color: '#64748B', fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>
            {existing.status === 'approved' && 'Head to your dashboard to get your referral link and start earning.'}
            {existing.status === 'pending'  && "We'll review your application within 1–2 business days and notify you by email."}
            {existing.status === 'rejected' && "Unfortunately your application wasn't approved. You may re-apply below with updated information."}
            {existing.status === 'suspended' && 'Your affiliate account has been suspended. Please contact support for assistance.'}
          </p>
          {existing.status === 'approved' && (
            <Link to="/affiliates/dashboard" style={{ display: 'inline-block', padding: '12px 28px', background: '#1a7f4b', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>
              Go to Dashboard →
            </Link>
          )}
        </div>
      </Shell>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (done) {
    return (
      <Shell>
        <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0F172A', marginBottom: 12 }}>
            {done.status === 'approved' ? 'Welcome to the Program!' : 'Application Submitted!'}
          </h1>
          <p style={{ color: '#64748B', fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>{done.message}</p>
          {done.status === 'approved'
            ? <Link to="/affiliates/dashboard" style={{ display: 'inline-block', padding: '12px 28px', background: '#1a7f4b', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>Go to Affiliate Dashboard →</Link>
            : <Link to="/user-dashboard"       style={{ display: 'inline-block', padding: '12px 28px', background: '#1a7f4b', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>Back to My Dashboard →</Link>
          }
        </div>
      </Shell>
    );
  }

  // ── Application form (logged in, no existing application) ─────────────────
  return (
    <Shell>
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 24px 80px' }}>
        <Link to="/affiliates" style={{ fontSize: 13, color: '#64748B', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 28 }}>
          ← Back to Affiliate Program
        </Link>

        <h1 style={{ fontSize: 30, fontWeight: 900, color: '#0F172A', marginBottom: 6, letterSpacing: '-0.02em' }}>Apply to Be an Affiliate</h1>
        <p style={{ color: '#64748B', fontSize: 14, lineHeight: 1.7, marginBottom: 36 }}>
          Tell us a bit about yourself and how you plan to promote Safe Dividend Tracker. We review all applications within 1–2 business days.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Applying-as banner */}
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#64748B' }}>
            Applying as <strong style={{ color: '#0F172A' }}>{user?.first_name} {user?.last_name}</strong> ({user?.email})
          </div>

          {/* Promotion type */}
          <div>
            <label style={labelStyle}>Primary promotion channel <span style={{ color: '#DC2626' }}>*</span></label>
            <select value={form.promotion_type} onChange={e => set('promotion_type', e.target.value)} required style={inputStyle}>
              <option value="">Select a channel…</option>
              {PROMOTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Website / URL */}
          <div>
            <label style={labelStyle}>Website, channel, or profile URL</label>
            <input type="url" value={form.website_url} onChange={e => set('website_url', e.target.value)}
              placeholder="https://yourblog.com" style={inputStyle} />
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Optional but recommended — helps us review faster.</div>
          </div>

          {/* How will you promote */}
          <div>
            <label style={labelStyle}>How do you plan to promote Safe Dividend Tracker? <span style={{ color: '#DC2626' }}>*</span></label>
            <textarea
              rows={5}
              value={form.promotion_method}
              onChange={e => set('promotion_method', e.target.value)}
              placeholder="e.g. I write a weekly dividend investing newsletter to 3,000 subscribers and plan to feature your tool in a dedicated review post…"
              required
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {/* Terms agreement */}
          <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.agreed} onChange={e => set('agreed', e.target.checked)}
              style={{ marginTop: 2, accentColor: '#1a7f4b', width: 16, height: 16, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
              I agree to the{' '}
              <Link to="/terms" target="_blank" style={{ color: '#1a7f4b', fontWeight: 600 }}>Terms of Service</Link>
              {' '}and understand that commissions are subject to the program's policies. I will not use spam, paid search on the brand name, or misleading promotions.
            </span>
          </label>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#DC2626' }}>{error}</div>
          )}

          <button type="submit" disabled={submitting}
            style={{ padding: '13px 0', background: submitting ? '#9CA3AF' : '#1a7f4b', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: submitting ? 'not-allowed' : 'pointer', marginTop: 4 }}>
            {submitting ? 'Submitting…' : 'Submit Application →'}
          </button>
        </form>
      </div>
    </Shell>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, color: '#0F172A', background: '#fff', outline: 'none', boxSizing: 'border-box' };

function Shell({ children }) {
  const { isAuthenticated } = useAuth();

  // Authenticated users get the full app header with navigation
  if (isAuthenticated) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
        <AppHeader />
        <main className="pt-16">{children}</main>
      </div>
    );
  }

  // Unauthenticated visitors get a minimal marketing header
  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '0 32px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo size={32} />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Safe Dividend Tracker</span>
        </Link>
        <Link to="/affiliates" style={{ fontSize: 13, color: '#64748B', textDecoration: 'none' }}>← Affiliate Program</Link>
      </header>
      {children}
    </div>
  );
}
